<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue';

const props = defineProps<{
  hour: number; // hour in 24h format (e.g. 0-23)
  minute: number; // minute (0-59)
  autoOkDelayMs?: number; // optional: milliseconds before auto-OK (default: 10000)
}>();

const isModalVisible = ref(false);
let showTimeout: ReturnType<typeof setTimeout> | null = null;
let autoOkTimeout: ReturnType<typeof setTimeout> | null = null;

// Default auto-OK after 10s if no input
const MODAL_AUTO_OK_DELAY = props.autoOkDelayMs ?? 10000;

function getMsUntilNextShow() {
  const now = new Date();
  const next = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate(),
    props.hour,
    props.minute,
    0,
    0,
  );
  if (next <= now) {
    // Schedule for tomorrow if "now" has passed today's show time
    next.setDate(next.getDate() + 1);
  }
  return next.getTime() - now.getTime();
}

function scheduleModal() {
  if (showTimeout) clearTimeout(showTimeout);
  const msUntilShow = getMsUntilNextShow();

  showTimeout = setTimeout(() => {
    isModalVisible.value = true;
    setTimeout(scheduleModal, 24 * 60 * 60 * 1000);
  }, msUntilShow);
}

function handleOk() {
  isModalVisible.value = false;
  window.location.reload();
}

function handleCancel() {
  isModalVisible.value = false;
}

watch(isModalVisible, (visible) => {
  if (visible) {
    autoOkTimeout = setTimeout(() => {
      handleOk();
    }, MODAL_AUTO_OK_DELAY);
  } else if (autoOkTimeout) {
    clearTimeout(autoOkTimeout);
    autoOkTimeout = null;
  }
});

onMounted(() => {
  scheduleModal();
});

onUnmounted(() => {
  if (showTimeout) clearTimeout(showTimeout);
  if (autoOkTimeout) clearTimeout(autoOkTimeout);
});
</script>

<template>
  <a-modal
    v-model:open="isModalVisible"
    title="Recarregar aplicação"
    :closable="false"
    :mask-closable="false"
    :keyboard="false"
    ok-text="Recarregar"
    cancel-text="Cancelar"
    @ok="handleOk"
    @cancel="handleCancel"
  >
    <p>
      A aplicação gostaria de recarregar a página para aplicar atualizações.<br />
      Esta página será recarregada automaticamente em
      {{ MODAL_AUTO_OK_DELAY / 1000 }} segundos caso nenhuma ação seja tomada.
    </p>
  </a-modal>
</template>
